import { memo as Memo, useMemo, useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';
import { CSSTransition } from 'react-transition-group';

//* HOC's
import withUIContext from '@context/consumerHOC/UIConsumer';

//* Helpers
import isMobile from '@helpers/isMobile';

//* JSONs
import config from '@lib/config.json';

//* Components
import Menu from '@components/global/Menu';
import SocIcon from '@components/global/SocIcon';
import ContactInformation from './ContactInformation';
import DarkModeSwitcher from '@components/global/DarkModeSwitcher';
import LanguageSwitcher from '@components/global/LanguageSwitcher';
import { MenuSvg1, MenuSvg2, MenuSvg3, MenuSvg4 } from '../MenuSvg';
import Text from '@/components/global/Text';

const MenuComponent = Memo((props) => {
	//! Refs
	let ref = useRef();
	let cardRef1 = useRef();
	let cardRef2 = useRef();
	let cardRef3 = useRef();
	let cardRef4 = useRef();
	const nodeRef = useRef(null);

	//! Routes from Config
	const routes = useMemo(() => config.routes.default, []);

	//! Big Menu Object
	const menuObj = useMemo(
		() => ({
			type: 'nav',
			contClass: 'menu-items-cont',
			itemClass: 'menu-item',
			items: [
				{
					name: routes['about-us'].name,
					path: routes['about-us'].path,
					svg: <MenuSvg1 ref={cardRef1} />,
				},
				{
					name: routes.contact.name,
					path: routes.contact.path,
					svg: <MenuSvg2 ref={cardRef2} />,
				},
				{
					name: routes.portfolio.name,
					path: routes.portfolio.path,
					svg: <MenuSvg3 ref={cardRef3} />,
				},
				{
					name: routes['services'].name,
					path: routes['services'].path,
					svg: <MenuSvg4 ref={cardRef4} />,
				},
			],
		}),
		[routes, MenuSvg1, MenuSvg2, MenuSvg3, MenuSvg4]
	);

	//! Blog and Awards Menu Object
	const menuObj2 = useMemo(
		() => ({
			type: 'nav',
			contClass: 'menu-items-cont2',
			itemClass: 'menu-item2',
			items: [
				{
					name: routes.team.name,
					path: routes.team.path,
				},
				{
					name: routes.awards.name,
					path: routes.awards.path,
				},
				{
					name: routes.blog.name,
					path: routes.blog.path,
				},
			],
		}),
		[routes]
	);

	//! Animation
	const anim = useMemo(() => gsap.timeline(), []);

	//! Default Positions
	const defaultPositions = useCallback(() => {
		if (cardRef1.current) {
			gsap.set(cardRef1.current, {
				scale: 0,
				yPercent: -120,
				rotation: 60,
				force3D: true,
			});
		}
		if (cardRef2.current) {
			gsap.set(cardRef2.current, {
				scale: 0,
				yPercent: -100,
				rotation: -50,
				force3D: true,
			});
		}
		if (cardRef3.current) {
			gsap.set(cardRef3.current, {
				scale: 0,
				rotation: -20,
				force3D: true,
			});
		}
		if (cardRef4.current) {
			gsap.set(cardRef4.current, {
				scale: 0,
				force3D: true,
			});
		}
	}, []);

	//! Hide Elements
	const hideElements = useCallback((k) => {
		gsap.killTweensOf([cardRef1.current, cardRef2.current, cardRef3.current, cardRef4.current]);
		if (k !== 1 && cardRef1.current?.parentNode?.firstChild) {
			gsap.to(cardRef1.current.parentNode.firstChild, { opacity: 0, duration: 0 });
		}
		if (k !== 2 && cardRef2.current?.parentNode?.firstChild) {
			gsap.to(cardRef2.current.parentNode.firstChild, { opacity: 0, duration: 0 });
		}

		if (k !== 1 && cardRef1.current) {
			gsap.to(cardRef1.current, {
				scale: 0,
				yPercent: -120,
				rotation: 60,
				duration: 0.5,
				force3D: true,
			});
		}
		if (k !== 2 && cardRef2.current) {
			gsap.to(cardRef2.current, {
				scale: 0,
				yPercent: -100,
				rotation: -20,
				duration: 0.5,
				force3D: true,
			});
		}
		if (k !== 3 && cardRef3.current) {
			gsap.to(cardRef3.current, {
				scale: 0,
				rotation: -30,
				duration: 0.3,
				force3D: true,
			});
		}
		if (k !== 4 && cardRef4.current) {
			gsap.to(cardRef4.current, {
				scale: 0,
				duration: 0.5,
				force3D: true,
			});
		}
	}, []);

	//! Start Animation
	const startAnimation = useCallback(() => {
		gsap.killTweensOf([cardRef1.current, cardRef2.current, cardRef3.current, cardRef4.current]);

		if (cardRef1.current?.parentNode?.firstChild) {
			gsap.to(cardRef1.current.parentNode.firstChild, {
				opacity: 1,
				duration: 0,
				delay: 0.4,
			});
		}
		if (cardRef2.current?.parentNode?.firstChild) {
			gsap.to(cardRef2.current.parentNode.firstChild, {
				opacity: 1,
				duration: 0,
				delay: 0.4,
			});
		}
		gsap.to('.navCont', {
			opacity: 1,
			duration: 0,
			delay: 0.2,
		});

		if (cardRef1.current) {
			gsap.to(cardRef1.current, {
				scale: 1,
				yPercent: 0,
				rotation: 0,
				duration: 1,
				delay: 0.1,
				ease: 'power3.out',
				force3D: true,
			});
		}
		if (cardRef2.current) {
			gsap.to(cardRef2.current, {
				scale: 1,
				yPercent: 0,
				rotation: 0,
				duration: 1,
				delay: 0.2,
				ease: 'power3.out',
				force3D: true,
			});
		}
		if (cardRef3.current) {
			gsap.to(cardRef3.current, {
				scale: 1,
				rotation: 0,
				duration: 1,
				delay: 0.3,
				ease: 'power3.out',
				force3D: true,
			});
		}
		if (cardRef4.current) {
			gsap.to(cardRef4.current, {
				scale: 1,
				duration: 1,
				delay: 0.4,
				ease: 'power3.out',
				force3D: true,
			});
		}
	}, []);

	const animateItems = useCallback((e) => {
		const minMaxDeg = 30;
		const beta = e.beta > minMaxDeg ? minMaxDeg : e.beta < -minMaxDeg ? -minMaxDeg : e.beta;
		const gamma = e.gamma > minMaxDeg ? minMaxDeg : e.gamma < -minMaxDeg ? -minMaxDeg : e.gamma;
		const sizeBeta = (beta / minMaxDeg) * -5;
		const sizeGamma = (gamma / minMaxDeg) * 10;
		// gsap.set('.navCont', {
		// 	x: sizeGamma,
		// 	y: sizeBeta,
		// });

		// gsap.set('.menu-item .svg-max', {
		// 	x: -sizeGamma,
		// 	y: -sizeBeta,
		// });
	}, []);

	useEffect(() => {
		if (props.opened) {
			requestAnimationFrame(() => {
				defaultPositions();
				startAnimation();
				gsap.to('.Linkmenu-item', {
					scale: 0.95,
					duration: 1,
					stagger: 0.2,
				});
				gsap.to('.language-socicon li, .contactInfoWrap *, .menu-items-cont2 *, .whiteIcon > a, .darkModeSwitcher', {
					opacity: 1,
					duration: 0,
					delay: 0.5,
					stagger: 0.1,
				});
			});
		} else {
			anim.clear();
		}
	}, [props.opened, props.winWidth, anim, defaultPositions, startAnimation]);

	const mouseOver = useCallback(
		(e, k) => {
			if (!isMobile()) {
				gsap.set('.navCont', { opacity: 0 });
				gsap.killTweensOf([cardRef1?.current, cardRef2?.current, cardRef3?.current, cardRef4?.current]);
				hideElements(k);
				const obj = e?.target?.getBoundingClientRect();
				const relX = e.pageX - obj.left;
				const cardRefs = [cardRef1, cardRef2, cardRef3, cardRef4];
				const el = cardRefs[k - 1]?.current;

				if (!el) return;

				const newX = (relX - obj.width) / 15;
				gsap.to(el, {
					scale: 1.1,
					yPercent: 0,
					rotation: 0,
					rotationX: ((relX - obj.width / 5) / obj.width) * 10,
					rotationZ: ((relX - obj.height / 20) / obj.height) * -1,
					x: newX,
					duration: 0.5,
					force3D: true,
				});
				if (el.parentNode?.childNodes?.[0]) {
					gsap.to(el.parentNode.childNodes[0], {
						x: newX * -1,
						duration: 0.5,
					});
					gsap.set(el.parentNode.childNodes[0], { opacity: 1 });
				}
			}
		},
		[hideElements]
	);

	const mouseOut = useCallback(() => {
		startAnimation();
	}, [startAnimation]);

	useEffect(() => {
		window.addEventListener('deviceorientation', animateItems, true);

		return () => {
			window.removeEventListener('deviceorientation', animateItems);
		};
	}, [animateItems]);

	return (
		<CSSTransition
			unmountOnExit
			nodeRef={nodeRef}
			in={props.opened}
			classNames='custom-fade'
			timeout={{ enter: 400, exit: 700 }}
		>
			<div
				ref={nodeRef}
				className='menuContainer'
			>
				<div
					ref={ref}
					className='lgMenuWrap'
				>
					<Menu
						onClick={props.onClick}
						onMouseMove={mouseOver}
						onMouseOut={mouseOut}
						svgLayer
						{...menuObj}
					/>
				</div>

				<div className='minMenuWrap'>
					{props.winWidth >= props.screenSizes.screenS ? (
						<>
							<div className='language-socicon'>{/* <LanguageSwitcher /> */}</div>

							<ContactInformation content={props.content} />

							<SocIcon
								type={2}
								whiteIcon
								menuIcon={false}
								isSocial
							/>

							<Menu
								onClick={props.onClick}
								{...menuObj2}
							/>

							<Text
								tag={'span'}
								className='darkModeSwitcher'
							>
								<DarkModeSwitcher />
							</Text>
						</>
					) : (
						<>
							<Menu
								{...menuObj2}
								onClick={props.onClick}
							/>

							<ContactInformation content={props.content} />

							<div className='language-socicon'>
								{/* <LanguageSwitcher /> */}

								<SocIcon
									type={2}
									whiteIcon
									menuIcon={false}
									isSocial
								/>

								<Text
									tag={'span'}
									className='darkModeSwitcher'
								>
									<DarkModeSwitcher />
								</Text>
							</div>
						</>
					)}
				</div>
			</div>
		</CSSTransition>
	);
});

export default withUIContext(MenuComponent, ['screenSizes', 'winWidth']);
